/**
 * Horizon Pro — Cart Drawer Smart Upsell
 * ─────────────────────────────────────────────────────────────
 * Responsibilities:
 *   1. +Add click handler — reads the variant from the card's inline
 *      <select> when present (multi-variant), otherwise the button's
 *      data-variant-id. POSTs to /cart/add.js with the cart section
 *      ids, refreshes /cart.js, dispatches `cart:update`. NEVER
 *      redirects to the product page.
 *   2. Variant <select> change handler — keeps the displayed price and
 *      the +Add button's data-variant-id in sync with the chosen option.
 *   3. JS-loaded recommendations fill — fills remaining slots with
 *      Shopify's COMPLEMENTARY recommendations
 *      (`/recommendations/products.json?intent=complementary`). Genuine
 *      companion products, not same-category duplicates. No best-seller
 *      fallback — if there are no smart matches, the row hides itself.
 *   4. Scroll-position dots.
 *
 * RL-FIX 2026-05-14 (merged onto Codex's section-handling rework):
 *   - intent=related → intent=complementary (smart upsells)
 *   - dropped best-seller fallback
 *   - inline variant <select> instead of PDP redirect
 *   - hide the whole .hp-upsell block when it ends up with 0 cards
 *
 * Survives drawer re-renders: delegated listeners on `document`, the
 * renderer re-runs each time a fresh .hp-upsell node enters the DOM.
 */

(function () {
  'use strict';

  if (window.__rlUpsellInit) return;
  window.__rlUpsellInit = true;

  const ADD_URL = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
  const CART_URL = ((window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart') + '.js';

  /* ─── Helpers ────────────────────────────────────────────── */

  function normalizeSectionId(sectionId) {
    return String(sectionId || '').replace(/^shopify-section-/, '');
  }

  function getCurrentCartCountFallback() {
    const bubble = document.querySelector('cart-icon [ref="cartBubbleCount"], [data-testid="cart-bubble"]');
    const count = parseInt((bubble && bubble.textContent) || '0', 10);
    return Number.isFinite(count) ? count : 0;
  }

  function getSectionsToUpdate(sourceRoot) {
    const ids = new Set();

    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (component) {
      if (component.dataset.sectionId) ids.add(normalizeSectionId(component.dataset.sectionId));
    });

    document.querySelectorAll('[data-hp-upsell]').forEach(function (upsellRoot) {
      const section = upsellRoot.closest('[id^="shopify-section-"]');
      if (section) ids.add(normalizeSectionId(section.id));
    });

    const sourceSection = sourceRoot && sourceRoot.closest && sourceRoot.closest('[id^="shopify-section-"]');
    if (sourceSection) ids.add(normalizeSectionId(sourceSection.id));

    return Array.from(ids).filter(Boolean).slice(0, 5);
  }

  function getSectionsUrl() {
    return window.location.pathname + window.location.search;
  }

  async function fetchCart() {
    const response = await fetch(CART_URL, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Cart refresh failed: ' + response.status);
    return response.json();
  }

  function dispatchCartUpdate(cart, sections) {
    const itemCount =
      cart && typeof cart.item_count === 'number'
        ? cart.item_count
        : getCurrentCartCountFallback() + 1;

    const ev = new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart || null,
        sourceId: 'hp-upsell',
        data: {
          itemCount: itemCount,
          sections: sections || {},
          source: 'hp-upsell',
        },
      },
    });
    document.dispatchEvent(ev);
  }

  function refreshStandaloneSections(sections) {
    if (!sections) return;

    Object.keys(sections).forEach(function (sectionId) {
      const existing = document.getElementById('shopify-section-' + normalizeSectionId(sectionId));
      if (!existing || existing.querySelector('cart-items-component')) return;

      const parsed = new DOMParser().parseFromString(sections[sectionId], 'text/html');
      const fresh = parsed.getElementById('shopify-section-' + normalizeSectionId(sectionId));
      if (!fresh) return;

      existing.replaceWith(fresh);
    });
  }

  /* RL-FIX 2026-05-14: hide the whole upsell row if it has zero cards —
     an empty "Pairs Well With Your Cart" header looks broken. The Liquid
     snippet renders the wrapper whenever the cart has items, so when no
     smart matches exist (no complementary metafield, no different-type
     products) we hide it here. */
  function maybeHideEmpty(root) {
    if (!root) return;
    const cards = root.querySelectorAll('.hp-upsell__card');
    if (cards.length === 0) {
      root.setAttribute('hidden', '');
      root.style.display = 'none';
    } else {
      root.removeAttribute('hidden');
      root.style.display = '';
    }
  }

  /* ─── Add to cart (single click) ─────────────────────────── */

  document.addEventListener('click', function (e) {
    const btn = e.target.closest && e.target.closest('[data-hp-upsell-add]');
    if (!btn || btn.disabled) return;

    e.preventDefault();
    e.stopPropagation();

    /* RL-FIX 2026-05-14: read the chosen variant from the card's inline
       <select> (multi-variant) — no PDP redirect. Single-variant cards
       fall back to the button's data-variant-id. */
    const card = btn.closest('[data-hp-upsell-card]');
    const variantSelect = card && card.querySelector('[data-hp-upsell-variant]');
    let variantId;
    if (variantSelect) {
      const opt = variantSelect.options[variantSelect.selectedIndex];
      if (opt && opt.disabled) return; /* sold-out option selected — ignore */
      variantId = variantSelect.value;
    } else {
      variantId = btn.getAttribute('data-variant-id');
    }
    if (!variantId) return;

    btn.disabled = true;
    btn.classList.add('is-loading');

    const sourceRoot = btn.closest('[data-hp-upsell]');
    const sectionIds = getSectionsToUpdate(sourceRoot);
    const body = {
      items: [{ id: parseInt(variantId, 10), quantity: 1 }],
    };
    if (sectionIds.length) {
      body.sections = sectionIds.join(',');
      body.sections_url = getSectionsUrl();
    }

    fetch(ADD_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
      },
      body: JSON.stringify(body),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Add failed: ' + r.status);
        return r.json();
      })
      .then(async function (data) {
        btn.classList.remove('is-loading');
        btn.classList.add('is-success');
        const svg = btn.querySelector('svg');
        if (svg) {
          svg.innerHTML =
            '<polyline points="20 6 9 17 4 12" stroke-linecap="round" stroke-linejoin="round" />';
        }

        const sections = (data && data.sections) || {};
        let cart = null;

        try {
          cart = await fetchCart();
        } catch (err) {
          console.warn('[hp-cart-upsell] cart refresh:', err);
        }

        dispatchCartUpdate(cart, sections);
        refreshStandaloneSections(sections);

        /* Reset button after a beat; the morph will likely have replaced
           it anyway — this is just defence-in-depth in case the drawer
           re-render is async. */
        setTimeout(function () {
          if (!document.body.contains(btn)) return;
          btn.classList.remove('is-success');
          btn.disabled = false;
          if (svg) {
            svg.innerHTML =
              '<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>';
          }
        }, 1600);
      })
      .catch(function (err) {
        btn.classList.remove('is-loading');
        btn.disabled = false;
        console.error('[hp-cart-upsell]', err);
      });
  });

  /* ─── Variant <select> change — sync price + button ──────── */

  document.addEventListener('change', function (e) {
    const sel = e.target.closest && e.target.closest('[data-hp-upsell-variant]');
    if (!sel) return;
    const card = sel.closest('[data-hp-upsell-card]');
    if (!card) return;
    const opt = sel.options[sel.selectedIndex];
    /* Update the visible price to the chosen variant's price. */
    const priceEl = card.querySelector('[data-hp-upsell-price]');
    if (priceEl && opt && opt.dataset.price) priceEl.textContent = opt.dataset.price;
    /* Keep the +Add button's data-variant-id in sync (the click handler
       reads the select directly, but this keeps the DOM consistent). */
    const addBtn = card.querySelector('[data-hp-upsell-add]');
    if (addBtn) addBtn.setAttribute('data-variant-id', sel.value);
  });

  /* ─── Scroll-position indicator dots ─────────────────────── */

  function bindScrollDots(root) {
    const scroller = root.querySelector('[data-hp-upsell-scroller]');
    const dotsWrap = root.querySelector('.hp-upsell__scroll-hint');
    if (!scroller || !dotsWrap) return;

    function updateDots() {
      const cards = scroller.querySelectorAll('.hp-upsell__card');
      const dots = dotsWrap.querySelectorAll('.hp-upsell__scroll-dot');
      if (!cards.length || !dots.length) return;
      const scrollLeft = scroller.scrollLeft;
      const scrollWidth = scroller.scrollWidth - scroller.clientWidth;
      if (scrollWidth <= 0) {
        dots.forEach(function (d, i) { d.classList.toggle('is-active', i === 0); });
        return;
      }
      const ratio = scrollLeft / scrollWidth;
      const activeIndex = Math.min(dots.length - 1, Math.round(ratio * (dots.length - 1)));
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === activeIndex);
      });
    }

    let raf = null;
    scroller.addEventListener(
      'scroll',
      function () {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(updateDots);
      },
      { passive: true }
    );

    /* If we have more dots than cards (e.g. JS-loaded slots are still empty),
       hide extras to keep the indicator honest. */
    const cards = scroller.querySelectorAll('.hp-upsell__card');
    const dots = dotsWrap.querySelectorAll('.hp-upsell__scroll-dot');
    dots.forEach(function (d, i) {
      d.style.display = i < Math.max(1, cards.length) ? '' : 'none';
    });
  }

  /* ─── JS recommendations fill ────────────────────────────── */

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMoney(cents) {
    try {
      return (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /* /recommendations/products.json returns storefront-style product JSON
     where variant.price is a decimal string ("629.00"). /products/X.js
     returns integer cents. Normalize both to integer cents. */
  function normalizeCents(price) {
    if (typeof price === 'number') return price;
    if (typeof price === 'string') {
      return price.indexOf('.') > -1
        ? Math.round(parseFloat(price) * 100)
        : parseInt(price, 10) || 0;
    }
    return 0;
  }

  /* RL-FIX 2026-05-14: client-rendered card now mirrors the server card —
     multi-variant products get an inline <select> so the shopper picks
     the variant in the side cart, no PDP redirect. */
  function renderClientCard(product) {
    const variants = product.variants || [];
    const firstAvailable = variants.filter(function (v) { return v.available; })[0] || variants[0];
    if (!firstAvailable) return '';

    const title = escapeHtml(product.title || '');
    const url = escapeHtml(product.url || (product.handle ? '/products/' + product.handle : '#'));
    const img = product.featured_image || (product.images && product.images[0]) || null;
    const imgUrl = img ? (typeof img === 'string' ? img : img.src || img) : '';
    const hasMulti = variants.length > 1;
    const priceStr = function (v) { return formatMoney(normalizeCents(v.price)); };

    let variantSelectHtml = '';
    if (hasMulti) {
      const opts = variants.map(function (v) {
        const sel = v.id === firstAvailable.id ? ' selected' : '';
        const dis = v.available ? '' : ' disabled';
        const label = escapeHtml(v.title || '') + (v.available ? '' : ' — Sold out');
        return '<option value="' + v.id + '" data-price="' + escapeHtml(priceStr(v)) + '"' + sel + dis + '>' + label + '</option>';
      }).join('');
      variantSelectHtml =
        '<select class="hp-upsell__variant-select" data-hp-upsell-variant aria-label="Choose an option for ' + title + '">' +
        opts +
        '</select>';
    }

    return (
      '<article class="hp-upsell__card' + (hasMulti ? ' hp-upsell__card--multi' : '') + '" data-hp-upsell-card data-product-id="' + product.id + '">' +
      '  <a href="' + url + '" class="hp-upsell__img" aria-label="' + title + '">' +
      (imgUrl ? '<img src="' + escapeHtml(imgUrl) + '" alt="' + title + '" width="56" height="56" loading="lazy">' : '') +
      '  </a>' +
      '  <div class="hp-upsell__info">' +
      '    <a href="' + url + '" class="hp-upsell__title">' + title + '</a>' +
      '    <p class="hp-upsell__price" data-hp-upsell-price>' + priceStr(firstAvailable) + '</p>' +
      variantSelectHtml +
      '  </div>' +
      '  <button type="button" class="hp-upsell__add-btn" data-hp-upsell-add data-variant-id="' + firstAvailable.id + '" data-product-url="' + url + '" data-has-multiple-variants="' + (hasMulti ? 'true' : 'false') + '" aria-label="Add ' + title + ' to cart">' +
      '    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '      <line x1="12" y1="5" x2="12" y2="19"/>' +
      '      <line x1="5" y1="12" x2="19" y2="12"/>' +
      '    </svg>' +
      '  </button>' +
      '</article>'
    );
  }

  async function fillJsSlot(root) {
    const slot = root.querySelector('[data-hp-upsell-js-slot]');
    if (!slot) { maybeHideEmpty(root); return; }
    const fillCount = parseInt(slot.dataset.fillCount, 10) || 0;
    if (fillCount <= 0) {
      slot.remove();
      maybeHideEmpty(root);
      return;
    }

    const cartPids = (slot.dataset.cartPids || '').toLowerCase();
    const seenPids = (slot.dataset.seenPids || '').toLowerCase();
    const sourceProductId = root.dataset.sourceProductId;

    let products = [];

    /* RL-FIX 2026-05-14: intent=complementary — Shopify's ML pairs genuine
       companion products (a bench/attachments for a rack, not another
       rack). No best-seller fallback: an empty row is better than a
       misleading "same machine, different brand" suggestion. */
    if (sourceProductId) {
      try {
        const recRes = await fetch(
          '/recommendations/products.json?product_id=' + sourceProductId +
          '&limit=' + (fillCount + 4) + '&intent=complementary'
        );
        if (recRes.ok) {
          const recData = await recRes.json();
          products = (recData && recData.products) || [];
        }
      } catch (e) { /* swallow — we'll just show fewer cards or none */ }
    }

    /* Filter out already-in-cart and already-rendered products */
    const filtered = products.filter(function (p) {
      const tag = '|' + p.id + '|';
      return !cartPids.includes(tag) && !seenPids.includes(tag);
    });

    if (!filtered.length) {
      slot.remove();
      maybeHideEmpty(root);
      return;
    }

    const html = filtered.slice(0, fillCount).map(renderClientCard).join('');
    if (!html) {
      slot.remove();
      maybeHideEmpty(root);
      return;
    }

    /* Replace the slot placeholder with the rendered cards */
    const wrap = document.createElement('div');
    wrap.innerHTML = html;
    while (wrap.firstChild) {
      slot.parentNode.insertBefore(wrap.firstChild, slot);
    }
    slot.remove();

    /* Re-init scroll dots so card count changes register */
    bindScrollDots(root);
    maybeHideEmpty(root);
  }

  /* ─── Initialization ─────────────────────────────────────── */

  function initRoot(root) {
    if (!root || root.__rlUpsellInited) return;
    root.__rlUpsellInited = true;
    bindScrollDots(root);
    /* Hide immediately if the server rendered an empty row; fillJsSlot
       will un-hide it if it manages to add cards. */
    maybeHideEmpty(root);
    fillJsSlot(root).catch(function (e) {
      console.warn('[hp-cart-upsell] fillJsSlot:', e);
      maybeHideEmpty(root);
    });
  }

  function initAll() {
    document.querySelectorAll('[data-hp-upsell]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Re-init after Horizon re-renders the cart drawer. */
  document.addEventListener('cart:update', function () {
    /* Run on the next tick so the morph has settled. */
    setTimeout(initAll, 50);
  });
})();
